// What an opened meeting and a row say beyond the meeting's status: the
// call link while it is ahead, whether it is a block on your own
// calendar, the one sentence a meeting without notes shows, what a
// recording's error, an upload that stopped and a retry say, a row's
// notes on the phone's Today, and the header's line (who follows up and
// how often). The Past list and the calendar are pastModel and
// calendar/calendarModel.
//
// Pure functions, no React. Self-test: listModel.selftest.ts.

import type { Meeting } from "@/api/meetings";
import { errorCode, isUnreachable } from "@/lib/api";
import { formatDateTime, joinList, pluralize, toDate } from "@/lib/format";
import {
  botStatus,
  followupNote,
  meetingStatus,
  openActionCount,
  recordingErrorShown,
  recordingInFlight,
  recordingStatus,
  worthPrompting,
  type MeetingStatus,
} from "./status";

/**
 * The call link, only while the meeting is ahead or under way: once it is
 * over a link to join is no use, and it is not a file.
 */
export function callLink(m: Pick<Meeting, "meet_link" | "status">): string | null {
  return m.status === "upcoming" && m.meet_link ? m.meet_link : null;
}

/**
 * A calendar block: from the calendar, nobody else, no call, no recording,
 * no notes ("Block", "Focus time"). Nobody asks about it; the list shows
 * it quietly.
 */
export function isBlock(m: Pick<Meeting, "source" | "notes_md" | "recording_path" | "transcript_path" | "attendees" | "meet_link">): boolean {
  return m.source === "calendar" && !m.notes_md && !worthPrompting(m);
}

/**
 * The one sentence an opened meeting without notes shows, or "" when
 * there is nothing to say (not happened yet, dismissed).
 */
export function noNotesLine(m: Meeting, agentName: string, followups: string | undefined, now?: Date): string {
  switch (m.status) {
    case "upcoming":
    case "skipped":
      return "";
    case "prompted": {
      const at = formatDateTime(m.prompted_at, { inline: true }, now);
      return `${agentName} asked about it in chat${at ? ` (${at})` : ""}. Reply there and the notes appear here.`;
    }
    case "ended":
      if (!worthPrompting(m)) return `Nobody else was in it, so ${agentName} won’t ask about it.`;
      return `No notes yet. ${followupNote(m, { agentName, followups, now })}.`;
    case "processed":
      return "No notes.";
  }
}

/** An upload counts as stopped when the row has not moved for this long (servers that do not say when the last part came). */
export const UPLOAD_STALL_MS = 2 * 3_600_000;
/** …and when no part has come for this long (the recorders send one every few seconds). */
export const UPLOAD_PAUSE_MS = 10 * 60_000;

type UploadFields = Pick<Meeting, "recording_status" | "updated_at"> & Partial<Pick<Meeting, "recording_last_part_at">>;

/** The app started uploading a recording and no audio has come for a while (the page was closed, the phone slept). */
export function uploadStalled(m: UploadFields, now: Date = new Date()): boolean {
  if (m.recording_status !== "uploading") return false;
  const last = toDate(m.recording_last_part_at);
  if (last) return now.getTime() - last.getTime() > UPLOAD_PAUSE_MS;
  const at = toDate(m.updated_at);
  return !!at && now.getTime() - at.getTime() > UPLOAD_STALL_MS;
}

/** Corvus finishes an upload left open on its own only within this long of its last audio (the server's sweeper). */
export const UPLOAD_SELF_FINISH_MS = 7 * 86_400_000;

/** What an opened meeting says about an upload that stopped: the audio that arrived is not lost. */
export function stalledLine(m: UploadFields, now: Date = new Date()): string {
  const last = toDate(m.recording_last_part_at ?? m.updated_at);
  const at = formatDateTime(m.recording_last_part_at ?? m.updated_at, { inline: true }, now);
  const since = `No audio has come in${at ? ` since ${at}` : " for a while"}.`;
  if (last && now.getTime() - last.getTime() > UPLOAD_SELF_FINISH_MS) {
    // The sweeper leaves an upload this old alone: only the device that recorded it finishes it now.
    return `${since} What arrived is kept, but Corvus no longer finishes it on its own: open Record on the device that was recording to finish it.`;
  }
  return `${since} What arrived is kept: Corvus finishes it and writes it up on its own. To add to it, open Record on the device that was recording.`;
}

/** The recording's own facts, in words: how many parts, what was not recorded, whether it was cut short. */
export function recordingFacts(m: Pick<Meeting, "recording_segments" | "recording_cut_short">): string {
  const segs = m.recording_segments ?? [];
  const out: string[] = [];
  if (segs.length > 1) {
    const gap = segs.reduce((n, s) => n + (s.gap_before_sec ?? 0), 0);
    const min = Math.round(gap / 60);
    out.push(`Recorded in ${segs.length} parts${min >= 1 ? `, with ${min} min not recorded between them` : ""}.`);
  }
  // A cut can be in the middle (carried on afterwards) or at the end: never claimed to be one or the other.
  if (m.recording_cut_short) out.push("It stopped at least once without Stop being pressed, so part of the meeting may be missing.");
  return out.join(" ");
}

/** A recording that is ready while part of it has no transcript (I2): the plain note Corvus gives, else null. */
export function readyRecordingNote(m: Pick<Meeting, "recording_status" | "recording_error">): string | null {
  if (m.recording_status !== "ready") return null;
  const note = (m.recording_error ?? "").trim();
  return note || null;
}

/**
 * What an opened meeting says about its recording's error, the same on the
 * desktop's meeting page and the phone's meeting item: a ready recording's
 * note (I2, secondary), a failed one's failure, and whether Retry
 * transcription is offered (recordingErrorShown: never beside "Corvus tries
 * again by itself", never for no speech or missing audio). Nothing while it
 * uploads or when the server has no audio.
 */
export function recordingErrorView(m: Pick<Meeting, "recording_status" | "recording_error" | "recording_path">): { note: string | null; failure: string | null; retry: boolean } {
  const none = { note: null, failure: null, retry: false };
  if (!m.recording_path || m.recording_status === "uploading") return none;
  const shown = recordingErrorShown(m.recording_error);
  if (m.recording_status === "ready") return { note: shown?.text ?? null, failure: null, retry: shown?.retry ?? false };
  if (m.recording_status === "failed") return { note: null, failure: shown ? `Transcription failed. ${shown.text}` : "Transcription failed.", retry: shown?.retry ?? true };
  return none;
}

/** Why Retry transcription did not start, in words (it never "failed to save"). */
export function retryErrorLine(e: unknown): string {
  if (isUnreachable(e)) return "Can't reach Corvus. Check your connection, then try again.";
  const code = errorCode(e);
  if (code === "transcribing") return "Corvus is transcribing it already. The notes follow by themselves.";
  if (code === "no_recording") return "Corvus no longer has this recording's audio, so it can't be transcribed again.";
  return "Couldn't start the transcription again. Try again in a minute.";
}

/**
 * What a row says beside the meeting, beyond its section or tab: a
 * recording or notes-bot state, a meeting under way or about to start,
 * when the agent asked about it, a ready transcript, open actions. Empty
 * for the usual case (not asked yet, notes done and ticked off,
 * dismissed, a meeting next week).
 */
export function rowNotes(m: Meeting, opts: { agentName?: string; now?: Date } = {}): MeetingStatus[] {
  const out: MeetingStatus[] = [];
  const rec = uploadStalled(m, opts.now)
    ? { label: "Upload paused", tone: "warning" as const, hint: "No audio has come in for a while; what arrived is finished and written up on its own" }
    : recordingStatus(m);
  if (rec && !(m.status === "skipped" && rec.tone === "danger")) out.push(rec);
  switch (m.status) {
    case "upcoming": {
      // Under way or about to start (not for a block); a recording's own badge says more.
      const s = meetingStatus(m, opts);
      if (s.tone === "info" && !rec && !isBlock(m)) out.push(s);
      break;
    }
    case "ended":
      // Not asked yet is the usual case (the tooltip and the details say when it will be).
      if (recordingInFlight(m) || m.recording_status === "failed" || !worthPrompting(m)) break;
      if (m.transcript_path || m.recording_status === "ready") out.push({ label: "Transcript ready", tone: "info" });
      break;
    case "processed":
    case "skipped": {
      const n = openActionCount(m);
      if (n) out.push({ label: pluralize(n, "open action"), tone: "neutral" });
      break;
    }
    case "prompted": {
      // The agent asked: the reply is the operator's to give, so this one is always said.
      if (recordingInFlight(m) || m.recording_status === "failed") break;
      const at = formatDateTime(m.prompted_at, { inline: true }, opts.now);
      out.push({ label: at ? `Asked ${at}` : "Asked in chat", tone: "neutral" });
      break;
    }
  }
  // The notes bot last: "Asked yesterday 03:06 · Bot transcript", the reply first.
  const bot = botStatus(m);
  if (bot) out.push(bot);
  return out;
}

const ASKS: Record<string, string> = {
  daily: "once a day",
  each: "after each one",
};

/**
 * The header's one quiet line: who follows up and how often.
 * "Julia asks about your meetings once a day"; "Julia and Soren ask …";
 * "Julia asks about your meetings once a day, Soren after each one";
 * "Julia writes up recordings; follow-ups are off". "" with no agent.
 */
export function followupLine(agents: readonly string[], followups: Readonly<Record<string, string>> | undefined, nameOf: (t: string) => string): string {
  const mode = (t: string) => (followups?.[t] && followups[t] in { ...ASKS, off: 1 } ? followups[t] : "daily");
  const asking = agents.filter((t) => mode(t) !== "off");
  const quiet = agents.filter((t) => mode(t) === "off");
  const parts: string[] = [];
  if (asking.length) {
    const modes = [...new Set(asking.map(mode))];
    if (modes.length === 1) {
      const names = asking.map(nameOf);
      parts.push(`${joinList(names)} ${names.length === 1 ? "asks" : "ask"} about your meetings ${ASKS[modes[0]]}`);
    } else {
      const [first, ...rest] = asking;
      parts.push(`${nameOf(first)} asks about your meetings ${ASKS[mode(first)]}, ${rest.map((t) => `${nameOf(t)} ${ASKS[mode(t)]}`).join(", ")}`);
    }
  }
  if (quiet.length) {
    const names = quiet.map(nameOf);
    parts.push(`${joinList(names)} ${names.length === 1 ? "writes" : "write"} up recordings; follow-ups are off`);
  }
  return parts.join(" · ");
}
